import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PortalAuthProvider, usePortalAuth } from "@/context/PortalAuthContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Alumnos from "@/pages/Alumnos";
import AlumnoDetalle from "@/pages/AlumnoDetalle";
import Clases from "@/pages/Clases";
import Profesores from "@/pages/Profesores";
import Sedes from "@/pages/Sedes";
import Usuarios from "@/pages/Usuarios";
import Padres from "@/pages/Padres";
import Planes from "@/pages/Planes";
import PagosFacturacion from "@/pages/PagosFacturacion";
import Manuales from "@/pages/Manuales";
import Calendario from "@/pages/Calendario";
import PasarLista from "@/pages/PasarLista";
import Comunicaciones from "@/pages/Comunicaciones";
import Mensajes from "@/pages/Mensajes";
import FichaProfesor from "@/pages/FichaProfesor";
import BuscarAlumno from "@/pages/BuscarAlumno";
import Recepcion from "@/pages/Recepcion";
import Legal from "@/pages/Legal";
import LegalAdmin from "@/pages/LegalAdmin";
import { IdiomaProvider } from "@/context/IdiomaContext";
import PortalHome from "@/pages/portal/PortalHome";
import PortalRegistro from "@/pages/portal/PortalRegistro";
import PortalRestablecer from "@/pages/portal/PortalRestablecer";
import Consentimiento from "@/pages/Consentimiento";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import { PagoExito, PagoCancelado } from "@/pages/portal/PagoResultado";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Alias que preserva el querystring: /portal/registro?sede=X -> /registro?sede=X
const PreserveSearchNavigate = ({ to }) => {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}${loc.hash}`} replace />;
};


const Cargando = () => (
  <div className="min-h-screen grid place-items-center text-muted-foreground">Cargando…</div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Cargando />;
  if (!user) return <Navigate to="/admin" replace />;
  return children;
};

const Redirigir = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === "profesor") return <Navigate to="/admin/pasar-lista" replace />;
  return children;
};

const StaffLogin = () => {
  const { user, loading } = useAuth();
  if (loading) return <Cargando />;
  if (user) return <Navigate to="/admin/panel" replace />;
  return <Login />;
};

const PortalProtected = ({ children }) => {
  const { data, loading } = usePortalAuth();
  if (loading) return <Cargando />;
  if (!data) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <BrowserRouter>
        <PortalAuthProvider>
          <IdiomaProvider>
          <AuthProvider>
            <Routes>
              {/* Familias */}
              <Route path="/" element={<PortalHome />} />
              <Route path="/registro" element={<PortalRegistro />} />
              <Route path="/restablecer" element={<PortalRestablecer />} />
              <Route path="/consentimiento/:token" element={<Consentimiento />} />
              <Route path="/portal" element={<PortalProtected><PortalDashboard /></PortalProtected>} />
              <Route path="/portal/pago/exito" element={<PortalProtected><PagoExito /></PortalProtected>} />
              <Route path="/portal/pago/cancelado" element={<PortalProtected><PagoCancelado /></PortalProtected>} />
              <Route path="/portal/login" element={<Navigate to="/" replace />} />
              <Route path="/portal/registro" element={<PreserveSearchNavigate to="/registro" />} />

              {/* Textos legales (públicos, multiidioma) */}
              <Route path="/legal/:slug" element={<Legal />} />

              {/* Recepción (tablet en la entrada, PIN por sede) */}
              <Route path="/recepcion" element={<Recepcion />} />

              {/* Panel interno */}
              <Route path="/admin" element={<StaffLogin />} />
              <Route path="/login" element={<Navigate to="/admin" replace />} />
              <Route path="/admin" element={<Protected><Layout /></Protected>}>
                <Route path="panel" element={<Redirigir><Dashboard /></Redirigir>} />
                <Route path="alumnos" element={<Redirigir><Alumnos /></Redirigir>} />
                <Route path="alumnos/:id" element={<Redirigir><AlumnoDetalle /></Redirigir>} />
                <Route path="calendario" element={<Calendario />} />
                <Route path="pasar-lista" element={<PasarLista />} />
                <Route path="clases" element={<Clases />} />
                <Route path="profesores" element={<Redirigir><Profesores /></Redirigir>} />
                <Route path="padres" element={<Redirigir><Padres /></Redirigir>} />
                <Route path="planes" element={<Redirigir><Planes /></Redirigir>} />
                <Route path="pagos" element={<Redirigir><PagosFacturacion /></Redirigir>} />
                <Route path="mensajes" element={<Mensajes />} />
                <Route path="ficha/:id" element={<FichaProfesor />} />
                <Route path="buscar-alumno" element={<BuscarAlumno />} />
                <Route path="manuales" element={<Manuales />} />
                <Route path="comunicaciones" element={<Redirigir><Comunicaciones /></Redirigir>} />
                <Route path="legal" element={<Redirigir><LegalAdmin /></Redirigir>} />
                <Route path="sedes" element={<Redirigir><Sedes /></Redirigir>} />
                <Route path="usuarios" element={<Redirigir><Usuarios /></Redirigir>} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </AuthProvider>
          </IdiomaProvider>
        </PortalAuthProvider>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
